import { type FormEvent, useEffect, useMemo, useState } from "react";
import type { Locale } from "./i18n";

type Copy = (key: string, values?: Record<string, string | number>) => string;

const API_ORIGIN = (import.meta.env.VITE_API_ORIGIN ?? "").replace(/\/$/, "");

export type ProgramType = "visit_stamp" | "item_stamp";

export interface ProgramLocalization {
  name: string;
  description: string | null;
  rewardLabel: string;
  terms: string | null;
}

export interface ProgramItemGroup {
  key: string;
  localizations: {
    en: string;
    "zh-Hant"?: string;
  };
}

export interface InterimReward {
  stamps: number;
  localizations: { en: string; "zh-Hant"?: string };
}

export interface ProgramDraftInput {
  type: ProgramType;
  threshold: number;
  earnQuantity: number;
  rewardExpiryDays: number | null;
  dailyEarnLimit: number | null;
  itemGroups: ProgramItemGroup[];
  interimRewards?: InterimReward[];
  localizations: {
    en: ProgramLocalization;
    "zh-Hant"?: ProgramLocalization;
  };
}

export interface ProgramVersion {
  id: string;
  version: number;
  type: ProgramType | "spend_points";
  threshold: number;
  earnQuantity: number;
  name: string;
  description: string | null;
  rewardLabel: string;
  terms: string | null;
  itemGroups: Array<{ key: string; label: string }>;
  interimRewards?: Array<{ stamps: number; label: string }>;
  effectiveFrom: string;
}

export interface ProgramDraftProjection extends ProgramDraftInput {
  id: string;
  fallbackLocale: "en";
  missingLocales: Array<"zh-Hant">;
  updatedAt: string;
  activeVersion: ProgramVersion | null;
  publishedVersions: ProgramVersion[];
  idempotentReplay: boolean;
}

export type ProgramBuilderState =
  | { status: "loading" }
  | { status: "load-error" }
  | { status: "forbidden" }
  | { status: "ready"; draft: ProgramDraftProjection | null };

interface ProblemBody {
  error?: {
    code?: string;
    fieldIssues?: Array<{ path: string; code: string }>;
  };
}

export function emptyProgramDraftInput(): ProgramDraftInput {
  return {
    type: "visit_stamp",
    threshold: 8,
    earnQuantity: 1,
    rewardExpiryDays: 60,
    dailyEarnLimit: 3,
    interimRewards: [],
    itemGroups: [],
    localizations: {
      en: {
        name: "",
        description: null,
        rewardLabel: "",
        terms: null,
      },
    },
  };
}

function nullableText(value: string | null): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed ? trimmed : null;
}

function cleanLocalization(value: ProgramLocalization): ProgramLocalization {
  return {
    name: value.name.trim(),
    description: nullableText(value.description),
    rewardLabel: value.rewardLabel.trim(),
    terms: nullableText(value.terms),
  };
}

function hasLocalizationContent(value: ProgramLocalization): boolean {
  return Boolean(
    value.name.trim() ||
    value.rewardLabel.trim() ||
    value.description?.trim() ||
    value.terms?.trim(),
  );
}

export function normalizeProgramDraft(
  input: ProgramDraftInput,
): ProgramDraftInput {
  const chinese = input.localizations["zh-Hant"];
  return {
    ...input,
    interimRewards: (input.interimRewards ?? []).map((reward) => ({
      stamps: reward.stamps,
      localizations: {
        en: reward.localizations.en.trim(),
        ...(reward.localizations["zh-Hant"]?.trim()
          ? { "zh-Hant": reward.localizations["zh-Hant"].trim() }
          : {}),
      },
    })),
    itemGroups:
      input.type === "item_stamp"
        ? input.itemGroups.map((group) => ({
            key: group.key.trim(),
            localizations: {
              en: group.localizations.en.trim(),
              ...(group.localizations["zh-Hant"]?.trim()
                ? {
                    "zh-Hant": group.localizations["zh-Hant"].trim(),
                  }
                : {}),
            },
          }))
        : [],
    localizations: {
      en: cleanLocalization(input.localizations.en),
      ...(chinese && hasLocalizationContent(chinese)
        ? { "zh-Hant": cleanLocalization(chinese) }
        : {}),
    },
  };
}

export function validateProgramDraft(
  rawInput: ProgramDraftInput,
): Record<string, string> {
  const input = normalizeProgramDraft(rawInput);
  const issues: Record<string, string> = {};
  if (
    !Number.isSafeInteger(input.threshold) ||
    input.threshold < 2 ||
    input.threshold > 100
  ) {
    issues["/threshold"] = "range";
  }
  if (
    !Number.isSafeInteger(input.earnQuantity) ||
    input.earnQuantity < 1 ||
    input.earnQuantity > 20
  ) {
    issues["/earnQuantity"] = "range";
  }
  if (
    input.rewardExpiryDays !== null &&
    (!Number.isSafeInteger(input.rewardExpiryDays) ||
      input.rewardExpiryDays < 1 ||
      input.rewardExpiryDays > 730)
  ) {
    issues["/rewardExpiryDays"] = "range";
  }
  if (
    input.dailyEarnLimit !== null &&
    (!Number.isSafeInteger(input.dailyEarnLimit) ||
      input.dailyEarnLimit < 1 ||
      input.dailyEarnLimit > 100)
  ) {
    issues["/dailyEarnLimit"] = "range";
  }

  const english = input.localizations.en;
  if (!english.name) issues["/localizations/en/name"] = "required";
  if (english.name.length > 80) {
    issues["/localizations/en/name"] = "length";
  }
  if (!english.rewardLabel) {
    issues["/localizations/en/rewardLabel"] = "required";
  }
  if (english.rewardLabel.length > 100) {
    issues["/localizations/en/rewardLabel"] = "length";
  }
  if ((english.description?.length ?? 0) > 240) {
    issues["/localizations/en/description"] = "length";
  }
  if ((english.terms?.length ?? 0) > 1_000) {
    issues["/localizations/en/terms"] = "length";
  }

  const chinese = input.localizations["zh-Hant"];
  if (chinese) {
    if (!chinese.name) {
      issues["/localizations/zh-Hant/name"] = "required";
    }
    if (!chinese.rewardLabel) {
      issues["/localizations/zh-Hant/rewardLabel"] = "required";
    }
    if (chinese.name.length > 80) {
      issues["/localizations/zh-Hant/name"] = "length";
    }
    if (chinese.rewardLabel.length > 100) {
      issues["/localizations/zh-Hant/rewardLabel"] = "length";
    }
    if ((chinese.description?.length ?? 0) > 240) {
      issues["/localizations/zh-Hant/description"] = "length";
    }
    if ((chinese.terms?.length ?? 0) > 1_000) {
      issues["/localizations/zh-Hant/terms"] = "length";
    }
  }

  if (input.type === "item_stamp") {
    if (input.itemGroups.length < 1 || input.itemGroups.length > 20) {
      issues["/itemGroups"] = "range";
    }
    const keys = new Set<string>();
    input.itemGroups.forEach((group, index) => {
      if (!/^[a-z0-9_]{2,40}$/.test(group.key)) {
        issues[`/itemGroups/${index}/key`] = "format";
      } else if (keys.has(group.key)) {
        issues[`/itemGroups/${index}/key`] = "duplicate";
      }
      keys.add(group.key);
      if (!group.localizations.en) {
        issues[`/itemGroups/${index}/localizations/en`] = "required";
      }
      if (group.localizations.en.length > 80) {
        issues[`/itemGroups/${index}/localizations/en`] = "length";
      }
      if ((group.localizations["zh-Hant"]?.length ?? 0) > 80) {
        issues[`/itemGroups/${index}/localizations/zh-Hant`] = "length";
      }
    });
  }
  if ((input.interimRewards?.length ?? 0) > 9)
    issues["/interimRewards"] = "range";
  let previous = 0;
  input.interimRewards?.forEach((reward, index) => {
    const path = `/interimRewards/${index}`;
    if (
      !Number.isSafeInteger(reward.stamps) ||
      reward.stamps <= previous ||
      reward.stamps >= input.threshold
    )
      issues[`${path}/stamps`] = "range";
    previous = reward.stamps;
    if (!reward.localizations.en)
      issues[`${path}/localizations/en`] = "required";
    for (const locale of ["en", "zh-Hant"] as const) {
      if ((reward.localizations[locale]?.length ?? 0) > 100)
        issues[`${path}/localizations/${locale}`] = "length";
    }
  });
  return issues;
}

function draftInput(draft: ProgramDraftProjection | null): ProgramDraftInput {
  if (!draft) return emptyProgramDraftInput();
  return {
    type: draft.type,
    threshold: draft.threshold,
    earnQuantity: draft.earnQuantity,
    rewardExpiryDays: draft.rewardExpiryDays,
    dailyEarnLimit: draft.dailyEarnLimit,
    itemGroups: draft.itemGroups,
    interimRewards: draft.interimRewards ?? [],
    localizations: draft.localizations,
  };
}

function fingerprint(input: ProgramDraftInput): string {
  return JSON.stringify(normalizeProgramDraft(input));
}

function requestKey(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

async function readProblem(response: Response): Promise<ProblemBody> {
  return response.json().catch(() => ({})) as Promise<ProblemBody>;
}

async function loadProgramDraft(
  merchantId: string,
): Promise<ProgramBuilderState> {
  try {
    const response = await fetch(`${API_ORIGIN}/v1/merchant/program-draft`, {
      credentials: "include",
      headers: { "x-merchant-id": merchantId },
    });
    if (response.ok) {
      return {
        status: "ready",
        draft: (await response.json()) as ProgramDraftProjection,
      };
    }
    if (response.status === 404) return { status: "ready", draft: null };
    if (response.status === 401 || response.status === 403) {
      return { status: "forbidden" };
    }
    return { status: "load-error" };
  } catch {
    return { status: "load-error" };
  }
}

function issueMessage(copy: Copy, code: string | undefined): string {
  return copy(
    code === "required"
      ? "merchant.program.validationRequired"
      : code === "format"
        ? "merchant.program.validationFormat"
        : code === "duplicate"
          ? "merchant.program.validationDuplicate"
          : code === "length"
            ? "merchant.program.validationLength"
            : "merchant.program.validationRange",
  );
}

export function ProgramBuilder({
  merchantId,
  locale,
  copy,
  initialState,
}: {
  merchantId: string;
  locale: Locale;
  copy: Copy;
  initialState?: ProgramBuilderState;
}) {
  const initialDraft =
    initialState?.status === "ready" ? initialState.draft : null;
  const [state, setState] = useState<ProgramBuilderState>(
    initialState ?? { status: "loading" },
  );
  const [input, setInput] = useState<ProgramDraftInput>(() =>
    draftInput(initialDraft),
  );
  const [baseline, setBaseline] = useState(() =>
    fingerprint(draftInput(initialDraft)),
  );
  const [issues, setIssues] = useState<Record<string, string>>({});
  const [operation, setOperation] = useState<"idle" | "saving" | "publishing">(
    "idle",
  );
  const [notice, setNotice] = useState<
    "saved" | "published" | "save-error" | "publish-error" | null
  >(null);
  const [confirmingPublish, setConfirmingPublish] = useState(false);
  const [saveKey, setSaveKey] = useState<string | null>(null);
  const [publishKey, setPublishKey] = useState<string | null>(null);

  const applyState = (next: ProgramBuilderState) => {
    setState(next);
    if (next.status === "ready") {
      const nextInput = draftInput(next.draft);
      setInput(nextInput);
      setBaseline(fingerprint(nextInput));
      setIssues({});
      setSaveKey(null);
    }
  };

  useEffect(() => {
    if (initialState) return;
    let active = true;
    void loadProgramDraft(merchantId).then((next) => {
      if (active) applyState(next);
    });
    return () => {
      active = false;
    };
  }, [initialState, merchantId]);

  const normalized = useMemo(() => normalizeProgramDraft(input), [input]);
  const dirty = fingerprint(input) !== baseline;
  const projection = state.status === "ready" ? state.draft : null;

  const change = (next: ProgramDraftInput) => {
    setInput(next);
    setNotice(null);
    setSaveKey(null);
    setPublishKey(null);
    setIssues({});
  };

  const updateLocalization = (
    targetLocale: "en" | "zh-Hant",
    field: keyof ProgramLocalization,
    value: string,
  ) => {
    const current = input.localizations[targetLocale] ?? {
      name: "",
      description: null,
      rewardLabel: "",
      terms: null,
    };
    change({
      ...input,
      localizations: {
        ...input.localizations,
        [targetLocale]: {
          ...current,
          [field]: value,
        },
      },
    });
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    const clientIssues = validateProgramDraft(input);
    if (Object.keys(clientIssues).length > 0) {
      setIssues(clientIssues);
      setNotice("save-error");
      return;
    }
    const key = saveKey ?? requestKey("program-draft-save");
    setSaveKey(key);
    setOperation("saving");
    setNotice(null);
    try {
      const response = await fetch(`${API_ORIGIN}/v1/merchant/program-draft`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "content-type": "application/json",
          "idempotency-key": key,
          "x-merchant-id": merchantId,
        },
        body: JSON.stringify(normalized),
      });
      if (!response.ok) {
        const problem = await readProblem(response);
        setIssues(
          Object.fromEntries(
            (problem.error?.fieldIssues ?? []).map((issue) => [
              issue.path,
              issue.code,
            ]),
          ),
        );
        setNotice("save-error");
        return;
      }
      const saved = (await response.json()) as ProgramDraftProjection;
      applyState({ status: "ready", draft: saved });
      setNotice("saved");
    } catch {
      setNotice("save-error");
    } finally {
      setOperation("idle");
    }
  };

  const publish = async () => {
    if (!projection || dirty) return;
    const key = publishKey ?? requestKey("program-draft-publish");
    setPublishKey(key);
    setConfirmingPublish(false);
    setOperation("publishing");
    setNotice(null);
    try {
      const response = await fetch(
        `${API_ORIGIN}/v1/merchant/program-draft/publish`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "content-type": "application/json",
            "idempotency-key": key,
            "x-merchant-id": merchantId,
          },
          body: JSON.stringify({
            effectiveFrom: new Date().toISOString(),
          }),
        },
      );
      if (!response.ok) {
        setNotice("publish-error");
        return;
      }
      const refreshed = await loadProgramDraft(merchantId);
      applyState(refreshed);
      setPublishKey(null);
      setNotice("published");
    } catch {
      setNotice("publish-error");
    } finally {
      setOperation("idle");
    }
  };

  if (state.status === "loading") {
    return (
      <section
        className="panel program-state"
        data-program-state="loading"
        aria-busy="true"
      >
        <div className="loading-mark" aria-hidden="true" />
        <p>{copy("merchant.program.loading")}</p>
      </section>
    );
  }
  if (state.status === "load-error") {
    return (
      <section
        className="panel program-state"
        data-program-state="error"
        role="alert"
      >
        <h2>{copy("merchant.program.loadError")}</h2>
        <button
          className="primary-button"
          type="button"
          onClick={() => {
            setState({ status: "loading" });
            void loadProgramDraft(merchantId).then(applyState);
          }}
        >
          {copy("common.retry")}
        </button>
      </section>
    );
  }
  if (state.status === "forbidden") {
    return (
      <section
        className="panel program-state"
        data-program-state="forbidden"
        role="alert"
      >
        <h2>{copy("merchant.program.permissionTitle")}</h2>
        <p>{copy("merchant.program.permissionBody")}</p>
      </section>
    );
  }

  const english = input.localizations.en;
  const chinese = input.localizations["zh-Hant"] ?? {
    name: "",
    description: null,
    rewardLabel: "",
    terms: null,
  };
  const previewCopy =
    locale === "zh-Hant" && input.localizations["zh-Hant"] ? chinese : english;
  const previewFallsBack =
    locale === "zh-Hant" && !input.localizations["zh-Hant"];
  const missingChinese = !input.localizations["zh-Hant"];

  return (
    <div className="program-builder" data-program-state="ready">
      <form className="program-editor" onSubmit={save}>
        <section className="panel editor-section">
          <div className="section-heading">
            <div>
              <p className="eyebrow">{copy("merchant.program.draft")}</p>
              <h2>{copy("merchant.program.templateHeading")}</h2>
            </div>
            {dirty && (
              <span className="draft-indicator">
                {copy("merchant.program.unsaved")}
              </span>
            )}
          </div>
          <div
            className="template-picker"
            role="group"
            aria-label={copy("merchant.program.type")}
          >
            {(["visit_stamp", "item_stamp"] as const).map((type) => (
              <button
                type="button"
                key={type}
                aria-pressed={input.type === type}
                onClick={() =>
                  change({
                    ...input,
                    type,
                    itemGroups:
                      type === "item_stamp" && input.itemGroups.length === 0
                        ? [
                            {
                              key: "hot_drinks",
                              localizations: { en: "" },
                            },
                          ]
                        : input.itemGroups,
                  })
                }
              >
                <strong>
                  {copy(
                    type === "visit_stamp"
                      ? "merchant.program.visitTemplate"
                      : "merchant.program.itemTemplate",
                  )}
                </strong>
                <span>
                  {copy(
                    type === "visit_stamp"
                      ? "merchant.program.visitHelp"
                      : "merchant.program.itemHelp",
                  )}
                </span>
              </button>
            ))}
          </div>
          <div className="numeric-grid">
            <NumberField
              id="program-threshold"
              label={copy("merchant.program.finalStamps")}
              value={input.threshold}
              min={2}
              max={100}
              issue={issues["/threshold"]}
              copy={copy}
              onChange={(threshold) =>
                change({
                  ...input,
                  threshold: threshold === "" ? input.threshold : threshold,
                })
              }
            />
            <NumberField
              id="program-daily-limit"
              label={copy("merchant.program.dailyLimit")}
              value={input.dailyEarnLimit ?? ""}
              min={1}
              max={100}
              issue={issues["/dailyEarnLimit"]}
              copy={copy}
              onChange={(dailyEarnLimit) =>
                change({
                  ...input,
                  dailyEarnLimit: dailyEarnLimit === "" ? null : dailyEarnLimit,
                })
              }
            />
            <NumberField
              id="program-expiry"
              label={copy("merchant.program.expiry")}
              value={input.rewardExpiryDays ?? ""}
              min={1}
              max={730}
              issue={issues["/rewardExpiryDays"]}
              copy={copy}
              onChange={(rewardExpiryDays) =>
                change({
                  ...input,
                  rewardExpiryDays:
                    rewardExpiryDays === "" ? null : rewardExpiryDays,
                })
              }
            />
          </div>
        </section>

        <LocalizationFields
          locale="en"
          title={copy("merchant.program.englishContent")}
          value={english}
          issues={issues}
          copy={copy}
          onChange={updateLocalization}
        />
        <LocalizationFields
          locale="zh-Hant"
          title={copy("merchant.program.chineseContent")}
          value={chinese}
          issues={issues}
          copy={copy}
          optional
          onChange={updateLocalization}
        />

        <section className="panel editor-section">
          <h2>{copy("merchant.program.interimHeading")}</h2>
          <p>{copy("merchant.program.interimHelp")}</p>
          {(input.interimRewards ?? []).map((reward, index) => {
            const update = (next: InterimReward) =>
              change({
                ...input,
                interimRewards: input.interimRewards!.map((candidate, i) =>
                  i === index ? next : candidate,
                ),
              });
            return (
              <fieldset
                className="localization-fields interim-reward-fields"
                key={index}
              >
                <legend>
                  {copy("merchant.program.interimNumber", {
                    number: index + 1,
                  })}
                </legend>
                <NumberField
                  id={`interim-${index}-stamps`}
                  label={copy("merchant.program.interimStamps")}
                  value={reward.stamps}
                  min={1}
                  max={input.threshold - 1}
                  issue={issues[`/interimRewards/${index}/stamps`]}
                  copy={copy}
                  onChange={(stamps) =>
                    update({ ...reward, stamps: stamps === "" ? 0 : stamps })
                  }
                />
                {(["en", "zh-Hant"] as const).map((language) => (
                  <TextField
                    key={language}
                    id={`interim-${index}-${language}`}
                    label={copy(
                      language === "en"
                        ? "merchant.program.interimEnglish"
                        : "merchant.program.interimChinese",
                    )}
                    value={reward.localizations[language] ?? ""}
                    maxLength={100}
                    issue={
                      issues[
                        `/interimRewards/${index}/localizations/${language}`
                      ]
                    }
                    copy={copy}
                    onChange={(label) =>
                      update({
                        ...reward,
                        localizations: {
                          ...reward.localizations,
                          [language]: label,
                        },
                      })
                    }
                  />
                ))}
                <button
                  type="button"
                  className="text-button"
                  onClick={() =>
                    change({
                      ...input,
                      interimRewards: input.interimRewards!.filter(
                        (_, i) => i !== index,
                      ),
                    })
                  }
                >
                  {copy("merchant.program.removeInterim")}
                </button>
              </fieldset>
            );
          })}
          <button
            type="button"
            className="quiet-button"
            disabled={(input.interimRewards?.length ?? 0) >= 9}
            onClick={() =>
              change({
                ...input,
                interimRewards: [
                  ...(input.interimRewards ?? []),
                  {
                    stamps: (input.interimRewards?.at(-1)?.stamps ?? 0) + 1,
                    localizations: { en: "" },
                  },
                ],
              })
            }
          >
            {copy("merchant.program.addInterim")}
          </button>
        </section>

        {input.type === "item_stamp" && (
          <ItemGroups
            groups={input.itemGroups}
            issues={issues}
            copy={copy}
            onChange={(itemGroups) => change({ ...input, itemGroups })}
          />
        )}

        {notice && (
          <p
            className={`operation-notice ${notice.includes("error") ? "error" : ""}`}
            role={notice.includes("error") ? "alert" : "status"}
          >
            {copy(
              notice === "save-error"
                ? "merchant.program.saveError"
                : notice === "publish-error"
                  ? "merchant.program.publishError"
                  : `merchant.program.${notice}`,
            )}
          </p>
        )}
        <div className="sticky-actions">
          <button
            className="quiet-button"
            type="submit"
            disabled={operation !== "idle" || !dirty}
          >
            {copy(
              operation === "saving"
                ? "merchant.program.saving"
                : "common.save",
            )}
          </button>
          <button
            className="primary-button"
            type="button"
            disabled={operation !== "idle" || dirty || !projection}
            onClick={() => setConfirmingPublish(true)}
          >
            {copy("common.publish")}
          </button>
        </div>
      </form>

      <div className="program-inspector">
        <section className="panel program-preview">
          <div className="section-heading">
            <div>
              <p className="eyebrow">{copy("merchant.program.preview")}</p>
              <h2>
                {previewCopy.name || copy("merchant.program.previewName")}
              </h2>
            </div>
            {previewFallsBack && (
              <span className="fallback-pill">
                {copy("merchant.program.fallbackPreview")}
              </span>
            )}
          </div>
          {missingChinese && (
            <p className="fallback-note">
              {copy("merchant.program.fallbackWarning")}
            </p>
          )}
          <div className="stamp-preview" aria-hidden="true">
            {Array.from(
              { length: Math.min(Math.max(input.threshold, 2), 12) },
              (_, index) => (
                <span className={index < 3 ? "filled" : ""} key={index} />
              ),
            )}
          </div>
          {(input.interimRewards ?? []).map((reward, index) => (
            <p key={index}>
              {copy("merchant.program.milestoneSummary", {
                stamps: new Intl.NumberFormat(locale).format(reward.stamps),
                reward:
                  (locale === "zh-Hant"
                    ? reward.localizations["zh-Hant"]
                    : undefined) ||
                  reward.localizations.en ||
                  copy("merchant.program.rewardLabel"),
              })}
            </p>
          ))}
          <strong>
            {input.interimRewards?.length
              ? copy("merchant.program.milestoneSummary", {
                  stamps: new Intl.NumberFormat(locale).format(input.threshold),
                  reward:
                    previewCopy.rewardLabel ||
                    copy("merchant.program.rewardLabel"),
                })
              : previewCopy.rewardLabel || copy("merchant.program.rewardLabel")}
          </strong>
          {previewCopy.description && <p>{previewCopy.description}</p>}
        </section>

        <section className="panel version-history">
          <div className="section-heading">
            <div>
              <p className="eyebrow">{copy("merchant.program.active")}</p>
              <h2>{copy("merchant.program.history")}</h2>
            </div>
          </div>
          {projection?.publishedVersions.length ? (
            <div className="version-list">
              {projection.publishedVersions.map((version) => (
                <article key={version.id}>
                  <div>
                    <strong>
                      {copy("merchant.program.version", {
                        version: version.version,
                      })}
                    </strong>
                    <span>
                      {new Intl.DateTimeFormat(locale, {
                        dateStyle: "medium",
                        timeStyle: "short",
                        timeZone: "UTC",
                      }).format(new Date(version.effectiveFrom))}
                    </span>
                  </div>
                  {version.interimRewards?.map((reward) => (
                    <p key={reward.stamps}>
                      {copy("merchant.program.milestoneSummary", {
                        stamps: new Intl.NumberFormat(locale).format(
                          reward.stamps,
                        ),
                        reward: reward.label,
                      })}
                    </p>
                  ))}
                  <input
                    aria-label={copy("merchant.program.publishedThreshold")}
                    value={version.threshold}
                    readOnly
                  />
                </article>
              ))}
            </div>
          ) : (
            <p className="empty-copy">{copy("merchant.program.noHistory")}</p>
          )}
        </section>
      </div>

      {confirmingPublish && (
        <div className="confirmation-backdrop">
          <section
            className="confirmation-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="publish-confirmation-title"
          >
            <p className="eyebrow">{copy("merchant.program.active")}</p>
            <h2 id="publish-confirmation-title">
              {copy("merchant.program.publishTitle")}
            </h2>
            <p>{copy("merchant.program.publishConfirm")}</p>
            <div className="form-actions">
              <button
                className="quiet-button"
                type="button"
                onClick={() => setConfirmingPublish(false)}
              >
                {copy("common.cancel")}
              </button>
              <button
                className="primary-button"
                type="button"
                onClick={() => void publish()}
              >
                {copy("common.publish")}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function NumberField({
  id,
  label,
  value,
  min,
  max,
  issue,
  copy,
  onChange,
}: {
  id: string;
  label: string;
  value: number | "";
  min: number;
  max: number;
  issue?: string | undefined;
  copy: Copy;
  onChange: (value: number | "") => void;
}) {
  return (
    <div className="field-control">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="number"
        min={min}
        max={max}
        value={value}
        aria-invalid={Boolean(issue)}
        onChange={(event) =>
          onChange(event.target.value === "" ? "" : Number(event.target.value))
        }
      />
      {issue && <small>{issueMessage(copy, issue)}</small>}
    </div>
  );
}

function LocalizationFields({
  locale,
  title,
  value,
  issues,
  copy,
  optional = false,
  onChange,
}: {
  locale: "en" | "zh-Hant";
  title: string;
  value: ProgramLocalization;
  issues: Record<string, string>;
  copy: Copy;
  optional?: boolean;
  onChange: (
    locale: "en" | "zh-Hant",
    field: keyof ProgramLocalization,
    value: string,
  ) => void;
}) {
  const prefix = `/localizations/${locale}`;
  return (
    <fieldset className="panel localization-fields">
      <legend>
        {title}
        {optional && <span>{copy("merchant.program.optional")}</span>}
      </legend>
      <TextField
        id={`program-${locale}-name`}
        label={copy("merchant.program.name")}
        value={value.name}
        maxLength={80}
        issue={issues[`${prefix}/name`]}
        copy={copy}
        onChange={(next) => onChange(locale, "name", next)}
      />
      <TextField
        id={`program-${locale}-description`}
        label={copy("merchant.program.description")}
        value={value.description ?? ""}
        maxLength={240}
        issue={issues[`${prefix}/description`]}
        copy={copy}
        multiline
        onChange={(next) => onChange(locale, "description", next)}
      />
      <TextField
        id={`program-${locale}-reward`}
        label={copy("merchant.program.finalReward")}
        value={value.rewardLabel}
        maxLength={100}
        issue={issues[`${prefix}/rewardLabel`]}
        copy={copy}
        onChange={(next) => onChange(locale, "rewardLabel", next)}
      />
      <TextField
        id={`program-${locale}-terms`}
        label={copy("merchant.program.terms")}
        value={value.terms ?? ""}
        maxLength={1_000}
        issue={issues[`${prefix}/terms`]}
        copy={copy}
        multiline
        onChange={(next) => onChange(locale, "terms", next)}
      />
    </fieldset>
  );
}

function TextField({
  id,
  label,
  value,
  maxLength,
  issue,
  copy,
  multiline = false,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  maxLength: number;
  issue?: string | undefined;
  copy: Copy;
  multiline?: boolean;
  onChange: (value: string) => void;
}) {
  const props = {
    id,
    value,
    maxLength,
    "aria-invalid": Boolean(issue),
    onChange: (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => onChange(event.target.value),
  };
  return (
    <div className="field-control">
      <label htmlFor={id}>{label}</label>
      {multiline ? <textarea {...props} rows={3} /> : <input {...props} />}
      <span className="field-meta">
        {issue ? issueMessage(copy, issue) : `${value.length}/${maxLength}`}
      </span>
    </div>
  );
}

function ItemGroups({
  groups,
  issues,
  copy,
  onChange,
}: {
  groups: ProgramItemGroup[];
  issues: Record<string, string>;
  copy: Copy;
  onChange: (groups: ProgramItemGroup[]) => void;
}) {
  const update = (index: number, next: ProgramItemGroup) =>
    onChange(
      groups.map((group, candidate) => (candidate === index ? next : group)),
    );
  return (
    <section className="panel item-groups">
      <div className="section-heading">
        <div>
          <p className="eyebrow">{copy("merchant.program.itemTemplate")}</p>
          <h2>{copy("merchant.program.itemGroups")}</h2>
        </div>
        <button
          className="quiet-button"
          type="button"
          disabled={groups.length >= 20}
          onClick={() =>
            onChange([
              ...groups,
              {
                key: `item_${groups.length + 1}`,
                localizations: { en: "" },
              },
            ])
          }
        >
          {copy("merchant.program.addItemGroup")}
        </button>
      </div>
      {issues["/itemGroups"] && (
        <p className="form-error">
          {issueMessage(copy, issues["/itemGroups"])}
        </p>
      )}
      {groups.map((group, index) => (
        <div className="item-group-row" key={`${group.key}-${index}`}>
          <TextField
            id={`item-${index}-key`}
            label={copy("merchant.program.itemKey")}
            value={group.key}
            maxLength={40}
            issue={issues[`/itemGroups/${index}/key`]}
            copy={copy}
            onChange={(key) => update(index, { ...group, key })}
          />
          <TextField
            id={`item-${index}-en`}
            label={copy("merchant.program.itemEnglish")}
            value={group.localizations.en}
            maxLength={80}
            issue={issues[`/itemGroups/${index}/localizations/en`]}
            copy={copy}
            onChange={(en) =>
              update(index, {
                ...group,
                localizations: { ...group.localizations, en },
              })
            }
          />
          <TextField
            id={`item-${index}-zh`}
            label={copy("merchant.program.itemChinese")}
            value={group.localizations["zh-Hant"] ?? ""}
            maxLength={80}
            issue={issues[`/itemGroups/${index}/localizations/zh-Hant`]}
            copy={copy}
            onChange={(value) =>
              update(index, {
                ...group,
                localizations: {
                  ...group.localizations,
                  "zh-Hant": value,
                },
              })
            }
          />
          <button
            className="text-button remove-group"
            type="button"
            onClick={() =>
              onChange(groups.filter((_, candidate) => candidate !== index))
            }
          >
            {copy("merchant.program.removeItemGroup")}
          </button>
        </div>
      ))}
    </section>
  );
}
